import { useEffect, useRef, useState } from "react";
import { fmtIST } from "@/shared/lib/time";
import api, { fmtErr, API } from "@/shared/lib/api";
import {
  Ruler, MapPin, Camera, CheckCircle2, Save, X, Upload,
  Image as ImageIcon, Pencil, Calculator,
} from "lucide-react";
import { toast } from "sonner";

/**
 * SiteMeasurementsTab — PSM-friendly entry of real-world dimensions for
 * every BOQ line of a project. Captures surface type, H × W, computed
 * area, remarks and optional site photos. Flips `site_audit=completed`.
 */
const SURFACE_TYPES = [
  { v: "wall",     label: "Wall (painted / plaster)" },
  { v: "glass",    label: "Glass façade / window" },
  { v: "acp",      label: "ACP / metal cladding" },
  { v: "drywall",  label: "Drywall / partition" },
  { v: "pillar",   label: "Pillar / column" },
  { v: "ceiling",  label: "Ceiling" },
  { v: "floor",    label: "Floor" },
  { v: "other",    label: "Other" },
];

const UNITS = ["ft", "in", "m", "cm"];

export default function SiteMeasurementsTab({ code, items, project, me, reload }) {
  const role = me?.role;
  const myId = me?.id;
  // Phase 34 r13 (Msg 813) — site measurements are STRICTLY PSM-only.
  const psmAssigned = (project?.assignments?.psm || []).includes(myId);
  const canEdit = role === "psm" ? psmAssigned : (role === "admin");

  // Msg 900 — the PSM measures WALLS only, never the main scope line. Show only
  // wall children, grouped under their parent scope line.
  const walls = (items || []).filter((l) => l.parent_id);
  const parents = (items || []).filter((l) => l.is_scope_parent);
  const groups = parents
    .map((p) => ({ parent: p, walls: walls.filter((w) => w.parent_id === p.id) }))
    .filter((g) => g.walls.length);

  if (!groups.length) {
    return (
      <div className="card-flat p-8 text-center text-stone-400" data-testid="site-tab">
        No walls to measure yet. The CSM sub-classifies a BOQ line into walls/glasses (in the BOQ tab) — then they appear here for measurement.
      </div>
    );
  }

  const measured = walls.filter((l) => l.site_measured_at || l.wall_measured).length;

  return (
    <div className="space-y-5" data-testid="site-tab">
      <div className="card-flat p-5 flex items-center gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center">
          <Ruler className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold tracking-tight text-lg">Wall Measurements</div>
          <div className="text-xs text-stone-500 mt-0.5">
            Capture real dimensions for each wall/glass. Saving completes its <b>Site Audit</b> and rolls the actuals up to the line's scope.
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-bold tabular-nums">{measured}<span className="text-stone-400 text-base">/{walls.length}</span></div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">walls measured</div>
        </div>
      </div>

      {groups.map((g) => (
        <div key={g.parent.id} className="space-y-3" data-testid={`site-group-${g.parent.id}`}>
          <div className="flex items-center gap-2 px-1">
            <span className="font-display font-bold tracking-tight">{g.parent.material_name}</span>
            <span className="text-[11px] font-mono uppercase tracking-widest text-stone-400">
              {g.parent.quantity_required} {g.parent.unit} scope · {g.walls.length} wall(s)
            </span>
          </div>
          {g.walls.map((line) => (
            <LineMeasurementCard key={line.id} code={code} line={line} canEdit={canEdit} reload={reload} />
          ))}
        </div>
      ))}
    </div>
  );
}

function LineMeasurementCard({ code, line, canEdit, reload }) {
  const hasMeasured = !!line.site_measured_at;
  const [editing, setEditing] = useState(!hasMeasured);

  return (
    <div className="card-flat p-5" data-testid={`site-line-${line.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          {line.wall_no && (
            <div className="font-mono text-[11px] text-orange-600">{line.wall_no}</div>
          )}
          <h4 className="font-display font-bold text-lg tracking-tight">{line.material_name}</h4>
          <div className="text-xs text-zinc-500 mt-0.5 font-mono">
            BOQ: {line.quantity_required} {line.unit}
            {line.height ? ` · planned ${line.height}″ × ${line.width}″` : ""}
          </div>
        </div>
        {hasMeasured && (
          <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[10px] font-mono uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="w-3 h-3" /> Measured
          </span>
        )}
      </div>

      {hasMeasured && !editing ? (
        <ReadOnlyView line={line} canEdit={canEdit} onEdit={() => setEditing(true)} />
      ) : (
        canEdit ? (
          <MeasurementForm code={code} line={line}
                           onClose={() => hasMeasured && setEditing(false)}
                           onSaved={() => { setEditing(false); reload(); }} />
        ) : (
          <div className="text-sm text-zinc-400 italic">PSM hasn&apos;t entered measurements yet.</div>
        )
      )}
    </div>
  );
}

function ReadOnlyView({ line, canEdit, onEdit }) {
  const photos = line.site_photos || [];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Surface" value={(line.site_surface_type || "").toUpperCase()} />
        <Stat label="Height"  value={`${line.site_actual_height} ${line.site_actual_unit}`} />
        <Stat label="Width"   value={`${line.site_actual_width} ${line.site_actual_unit}`} />
        <Stat label="Area"    value={`${line.site_actual_area || (line.site_actual_height * line.site_actual_width)} ${line.site_actual_unit}²`} highlight />
      </div>
      {line.site_remarks && (
        <div className="px-3 py-2 rounded-lg bg-amber-50/40 border border-amber-100 text-xs text-zinc-700">
          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mr-2">Remarks</span>
          {line.site_remarks}
        </div>
      )}
      {line.site_client_note && (
        <div className="px-3 py-2 rounded-lg bg-blue-50/50 border border-blue-200 text-xs text-zinc-700"
             data-testid={`site-client-note-ro-${line.id}`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-blue-700">Message to client</span>
            <span className={`text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${line.site_client_note_status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {line.site_client_note_status === "completed" ? "Client confirmed ✓" : "Awaiting client"}
            </span>
          </div>
          {line.site_client_note}
          {line.site_client_note_status === "completed" && line.site_client_note_response && (
            <div className="mt-1 pt-1 border-t border-blue-100 text-[11px] text-emerald-800">
              Client replied: &ldquo;{line.site_client_note_response}&rdquo;
            </div>
          )}
        </div>
      )}
      {photos.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            <ImageIcon className="w-3 h-3 inline mr-1" />Site photos · {photos.length}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                 className="block aspect-square rounded-lg overflow-hidden bg-zinc-100 ring-1 ring-zinc-200 hover:ring-amber-400">
                <img src={url} alt={`site-${i}`} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
        <div className="text-[10px] font-mono text-zinc-500">
          Measured by <b>{line.site_measured_by}</b> · {fmtIST(line.site_measured_at)}
        </div>
        {canEdit && (
          <button onClick={onEdit}
                  data-testid={`site-edit-${line.id}`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-zinc-300 text-xs font-medium hover:bg-zinc-50">
            <Pencil className="w-3.5 h-3.5" /> Update
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "bg-amber-50 border-amber-200" : "bg-zinc-50 border-zinc-100"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-base font-bold mt-0.5 ${highlight ? "text-amber-700" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function MeasurementForm({ code, line, onClose, onSaved }) {
  const [form, setForm] = useState({
    surface_type: line.site_surface_type || "wall",
    height: line.site_actual_height || "",
    width:  line.site_actual_width || "",
    unit:   line.site_actual_unit || "ft",
    remarks: line.site_remarks || "",
    client_note: line.site_client_note || "",
    mark_audit_complete: line.site_audit !== "completed" ? true : true,
  });
  const [photos, setPhotos] = useState(line.site_photos || []);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const area = (parseFloat(form.height) || 0) * (parseFloat(form.width) || 0);

  async function uploadPhoto(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, r.data.url]);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Photo upload failed");
    } finally { setUploading(false); }
  }

  async function save() {
    if (!form.height || !form.width || parseFloat(form.height) <= 0 || parseFloat(form.width) <= 0) {
      toast.error("Enter both height and width"); return;
    }
    try {
      await api.post(`/projects/${code}/boq/${line.id}/site-measurement`, {
        surface_type: form.surface_type,
        height: parseFloat(form.height),
        width: parseFloat(form.width),
        unit: form.unit,
        remarks: form.remarks,
        client_note: form.client_note,
        photos,
        mark_audit_complete: !!form.mark_audit_complete,
      });
      toast.success("Measurements saved");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Save failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Surface type" required>
          <select value={form.surface_type}
                  onChange={(e) => setForm({ ...form, surface_type: e.target.value })}
                  data-testid={`site-surface-${line.id}`}
                  className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm bg-white">
            {SURFACE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Unit" required>
          <select value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-300 rounded-lg text-sm bg-white">
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </Field>
        <Field label="Actual height" required>
          <div className="relative">
            <input type="number" step="0.01" min="0" value={form.height}
                   onChange={(e) => setForm({ ...form, height: e.target.value })}
                   data-testid={`site-h-${line.id}`}
                   className="w-full h-10 pl-3 pr-10 border border-zinc-300 rounded-lg text-sm" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-400">{form.unit}</span>
          </div>
        </Field>
        <Field label="Actual width" required>
          <div className="relative">
            <input type="number" step="0.01" min="0" value={form.width}
                   onChange={(e) => setForm({ ...form, width: e.target.value })}
                   data-testid={`site-w-${line.id}`}
                   className="w-full h-10 pl-3 pr-10 border border-zinc-300 rounded-lg text-sm" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-zinc-400">{form.unit}</span>
          </div>
        </Field>
      </div>

      {/* Computed area */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
        <Calculator className="w-5 h-5 text-amber-700" />
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">Total area</div>
          <div className="font-display text-2xl font-bold text-amber-900">
            {area > 0 ? `${area.toFixed(2)} ${form.unit}²` : "—"}
          </div>
        </div>
      </div>

      <Field label="Remarks (optional)">
        <textarea rows={2} value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  placeholder="e.g. wall has electrical conduit at 48&quot;, recommend offsetting logo 4&quot; right"
                  data-testid={`site-remarks-${line.id}`}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
      </Field>

      {/* Msg 887 — a SEPARATE note the client will see in their portal and act on. */}
      <Field label="Message to client (corrections needed at site)">
        <textarea rows={2} value={form.client_note}
                  onChange={(e) => setForm({ ...form, client_note: e.target.value })}
                  placeholder="Visible to the CLIENT in their portal — e.g. please remove the wall clock and clear the area below the false ceiling before installation."
                  data-testid={`site-client-note-${line.id}`}
                  className="w-full px-3 py-2 border border-blue-200 bg-blue-50/30 rounded-lg text-sm" />
        <div className="text-[10px] text-blue-700 mt-1">
          This appears in the client portal for the client to action &amp; confirm completion. Leave blank if there&apos;s nothing for the client to do.
        </div>
      </Field>

      {/* Photos */}
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Site photos
        </div>
        <input ref={fileInput} type="file" hidden accept="image/*" capture="environment"
               data-testid={`site-photo-input-${line.id}`}
               onChange={(e) => uploadPhoto(e.target.files?.[0])} />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {photos.map((url, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-zinc-100 ring-1 ring-zinc-200 group">
              <img src={url} alt={`photo-${i}`} className="w-full h-full object-cover" />
              <button onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 p-1 rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  data-testid={`site-add-photo-${line.id}`}
                  className="aspect-square rounded-lg border-2 border-dashed border-zinc-300 text-zinc-400 hover:border-amber-400 hover:text-amber-700 flex flex-col items-center justify-center text-[10px] font-medium">
            {uploading ? <Upload className="w-4 h-4 animate-pulse" /> : <Camera className="w-5 h-5" />}
            {uploading ? "Uploading…" : "Add"}
          </button>
        </div>
      </div>

      {/* Phase 34 r4 (Msg 749): saving measurements ALWAYS marks site_audit
          completed — no opt-out checkbox. The PSM's only action is to save. */}
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>Saving will <strong>mark Site Audit as completed</strong> for this BOQ line and notify Pre-Press automatically.</span>
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={save}
                data-testid={`site-save-${line.id}`}
                className="inline-flex items-center gap-1.5 px-5 h-10 rounded-full bg-amber-600 text-white text-sm font-medium hover:bg-amber-700">
          <Save className="w-4 h-4" /> Save measurements
        </button>
        {line.site_measured_at && (
          <button onClick={onClose}
                  className="px-4 h-10 rounded-full border border-zinc-300 text-sm font-medium hover:bg-zinc-50">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
        {label} {required && <span className="text-rose-600">*</span>}
      </div>
      {children}
    </div>
  );
}

void MapPin; void API; // silence unused-import lint when bundle prunes
